import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 10) {
      toast.error("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Welcome back.");
      // Backend also drops a fresh jwt cookie; hydrate the auth context
      // so route guards recognize us immediately.
      await refresh?.();
      navigate("/oracle");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  // Missing token — the user landed here without a link. Show a friendly
  // dead-end rather than an inscrutable form.
  if (!token) {
    return (
      <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 py-24">
        <div className="label mb-6">Threshold</div>
        <h1 className="serif text-5xl text-foreground leading-tight">The link is missing.</h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          This page needs a reset token from the email we sent. If you don't have one, request a fresh link.
        </p>
        <div className="mt-12 flex gap-6">
          <Link to="/forgot-password" data-testid="reset-request-new" className="btn-gold">Request a new link</Link>
          <Link to="/login" data-testid="reset-back-signin" className="btn-ghost">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 py-24">
      <div className="label mb-6">Threshold</div>
      <h1 className="serif text-5xl text-foreground leading-tight">Choose a new key.</h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">
        Set a fresh password — at least 10 characters. You'll be signed in once it's set.
      </p>

      <form onSubmit={onSubmit} className="space-y-8 mt-12">
        <div>
          <div className="label mb-3">New password</div>
          <input
            required
            type="password"
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="reset-password-input"
            className="input-line w-full"
          />
          <p className="mt-2 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            At least 10 characters
          </p>
        </div>
        <div>
          <div className="label mb-3">Confirm</div>
          <input
            required
            type="password"
            minLength={10}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-password-confirm"
            className="input-line w-full"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="reset-password-submit"
          className="btn-gold w-full disabled:opacity-40"
        >
          {busy ? "Setting…" : "Set new password"}
        </button>
      </form>

      <div className="mt-10 text-center">
        <Link
          to="/login"
          data-testid="reset-back-link"
          className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground link-underline"
        >
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
